<template>
  <div
    v-for="positionConfig in filteredPositionsConfig()"
    class="cnc_ad_container"
    :class="getClassList(positionConfig)"
  >
    <component :is="getComponent(positionConfig)" :payload="positionConfig" />
  </div>
</template>

<script setup lang="ts">
import type {PropType} from "vue"
import {useCncAdsWrapper} from "~/composables/useCncAdsWrapper"
import type {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {CncAdsPosition} from "~/libs/constants/CncAdsPosition"
import type CncAdPosition from "~/libs/interfaces/CncAdPosition"

const props = defineProps({
  position: {type: String as PropType<UniFeAdsPosition>, required: true},
  page: {type: String as PropType<CncAdsPage>, required: true},
  index: {type: Number, required: false, default: 0},
})

const filteredPositionsConfig = () => {
  return useCncAdsWrapper().getPositionsConfig(props.position, props.page).filter((config) => {
    return showPosition(config) && getComponent(config)
  })
}

const getComponent = (positionConfig?: CncAdPosition) => {
  switch (positionConfig?.cncPosition) {
    case CncAdsPosition.LEADERBOARD_1:
      return resolveComponent('DefaultAdsPositionsLeaderboard1')
    case CncAdsPosition.HALFPAGE_1:
      return resolveComponent('DefaultAdsPositionsHalfpage1')
    case CncAdsPosition.RECTANGLE_480_1:
      return resolveComponent('DefaultAdsPositionsRectangle4801')
    case CncAdsPosition.MOBILE_RECTANGLE_2:
      return resolveComponent('DefaultAdsPositionsMobileRectangle2')
    case CncAdsPosition.RECTANGLE_REPEATER_325:
      return resolveComponent('DefaultAdsPositionsRectangleRepeater325')
    // LOCAL STATIC
    case CncAdsPosition.LOCAL_LEADERBOARD_1:
      return resolveComponent('DefaultAdsPositionsLocalLeaderboard1')
    case CncAdsPosition.LOCAL_LEADERBOARD_2:
      return resolveComponent('DefaultAdsPositionsLocalLeaderboard2')
    case CncAdsPosition.LOCAL_HALFPAGE_1:
      return resolveComponent('DefaultAdsPositionsLocalHalfpage1')
    case CncAdsPosition.LOCAL_HALFPAGE_2:
      return resolveComponent('DefaultAdsPositionsLocalHalfpage2')
    // FROM API
    case CncAdsPosition.API_LEADERBOARD_1:
      return resolveComponent('DefaultAdsPositionsApiLeaderboard1')
    case CncAdsPosition.API_HALFPAGE_1:
      return resolveComponent('DefaultAdsPositionsApiHalfpage1')
  }
}

const showPosition = (positionConfig?: CncAdPosition): boolean => {
  if (!positionConfig) {
    return false
  }
  const offset =  positionConfig.offset || 0
  const repeatAfter = positionConfig.repeatAfter || 0

  if (offset === props.index) {
    return true
  }

  if (repeatAfter > 0 && (props.index - offset) >= (repeatAfter) && (props.index - offset) % (repeatAfter)  === 0) {
    return true
  }

  return false
}

const getClassList = (positionConfig?: CncAdPosition) => {
  const classList = [
    `cnc_ad_container--${props.position}`,
    `cnc_ad_container--${props.page}`
  ]

  if (positionConfig) {
    if (positionConfig.showOnDesktop && !positionConfig.showOnMobile) {
      classList.push(`cnc_ad_container--desktop-only`)
    }
    if (!positionConfig.showOnDesktop && positionConfig.showOnMobile) {
      classList.push(`cnc_ad_container--mobile-only`)
    }
  }
  return classList
}
</script>

<style lang="scss" scoped>
</style>